import { useCallback, useEffect } from 'react';
import { analytics, AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../services/analytics/AnalyticsService';
import { analyticsConfig } from '../config/analytics.config';

export interface UseAnalyticsReturn {
  // Core tracking methods
  trackEvent: (event: AnalyticsEvent) => void;
  trackSearch: (searchEvent: SearchEvent) => void;
  trackPlaylist: (playlistEvent: PlaylistEvent) => void;
  trackPlaylistCreationFailed: (artistName: string, errorType: string) => void;
  trackShare: (shareEvent: ShareEvent) => void;
  trackError: (error: Error, context?: Record<string, any>, severity?: ErrorEvent['severity']) => void;
  trackTiming: (category: string, variable: string, value: number, label?: string) => void;

  // Page tracking
  trackPageView: (path?: string, title?: string) => void;

  // Consent management
  hasConsent: () => boolean;
  setConsent: (consent: boolean) => void;
}

export const useAnalytics = (): UseAnalyticsReturn => {
  // Initialize analytics on mount
  useEffect(() => {
    analytics.initialize();
  }, []);

  // Track page view on mount
  useEffect(() => {
    analytics.trackPageView(window.location.pathname, document.title);
  }, []);

  // Core tracking methods
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    analytics.trackEvent(event);
  }, []);

  const trackSearch = useCallback((searchEvent: SearchEvent) => {
    analytics.trackSearch(searchEvent);
  }, []);

  const trackPlaylist = useCallback((playlistEvent: PlaylistEvent) => {
    analytics.trackPlaylist(playlistEvent);
  }, []);

  const trackPlaylistCreationFailed = useCallback((artistName: string, errorType: string) => {
    analytics.trackPlaylistCreationFailed(artistName, errorType);
  }, []);

  const trackShare = useCallback((shareEvent: ShareEvent) => {
    analytics.trackShare(shareEvent);
  }, []);

  const trackError = useCallback((
    error: Error,
    context?: Record<string, any>,
    severity: ErrorEvent['severity'] = 'medium'
  ) => {
    analytics.trackError({
      error,
      context,
      severity,
      errorInfo: {
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      }
    });
  }, []);

  const trackTiming = useCallback((
    category: string,
    variable: string,
    value: number,
    label?: string
  ) => {
    analytics.trackTiming(category, variable, value, label);
  }, []);

  const trackPageView = useCallback((path?: string, title?: string) => {
    analytics.trackPageView(path || window.location.pathname, title || document.title);
  }, []);

  const hasConsent = useCallback(() => {
    return analytics.hasConsent();
  }, []);

  const setConsent = useCallback((consent: boolean) => {
    analytics.setConsent(consent);
  }, []);

  return {
    trackEvent,
    trackSearch,
    trackPlaylist,
    trackPlaylistCreationFailed,
    trackShare,
    trackError,
    trackTiming,
    trackPageView,
    hasConsent,
    setConsent
  };
};

// Hook for tracking search performance
export const useSearchTracking = () => {
  const { trackSearch, trackTiming } = useAnalytics();
  
  const trackSearchWithTiming = useCallback((
    query: string,
    searchType: SearchEvent['searchType'],
    onSearch: () => Promise<{ artist?: string; resultsCount: number }>
  ) => {
    const startTime = performance.now();
    
    return onSearch().then(result => {
      const timeToResults = Math.round(performance.now() - startTime);
      
      trackSearch({
        query,
        artist: result.artist,
        resultsCount: result.resultsCount,
        searchType,
        timeToResults
      });
      
      trackTiming('search', 'time_to_results', timeToResults, searchType);
      
      return result;
    });
  }, [trackSearch, trackTiming]);
  
  return { trackSearchWithTiming };
};

// Hook for tracking share actions
export const useShareTracking = () => {
  const { trackShare } = useAnalytics();
  
  const trackShareAction = useCallback((
    method: ShareEvent['method'],
    artistName: string,
    playlistId?: string
  ) => {
    // Track share opened
    trackShare({
      method,
      artistName,
      playlistId,
      success: false // Initially false, will be updated on success
    });
    
    // Return a function to call on share success
    return () => {
      trackShare({
        method,
        artistName,
        playlistId,
        success: true
      });
    };
  }, [trackShare]);
  
  return { trackShareAction };
};

// Hook for playlist tracking
export const usePlaylistTracking = () => {
  const { trackPlaylist } = useAnalytics();
  
  const trackPlaylistAction = useCallback((
    action: PlaylistEvent['action'],
    artistName: string,
    artistId: string | undefined,
    songCount: number,
    platform: PlaylistEvent['platform'],
    playlistId?: string
  ) => {
    trackPlaylist({
      action,
      artistName,
      artistId,
      songCount,
      platform,
      playlistId
    });
  }, [trackPlaylist]);
  
  return { trackPlaylistAction };
};